/**
 * App Link Button Component
 *
 * A navigation button that routes within the Life Guide app.
 * Used in guidance content as CTAs.
 */

import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  ArrowRight,
  Plus,
  Check,
  Play,
  Eye,
  Edit,
  type LucideIcon,
} from 'lucide-react';

interface AppLinkButtonProps {
  label: string;
  route: string;
  style?: 'primary' | 'secondary' | 'ghost';
  icon?: string;
  className?: string;
}

// Map icon names to Lucide components
const iconMap: Record<string, LucideIcon> = {
  'arrow-right': ArrowRight,
  plus: Plus,
  check: Check,
  play: Play,
  eye: Eye,
  edit: Edit,
};

export function AppLinkButton({
  label,
  route,
  style = 'primary',
  icon,
  className,
}: AppLinkButtonProps) {
  const IconComponent = icon ? iconMap[icon] : null;

  const baseStyles =
    'inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200';

  const styleVariants = {
    primary:
      'bg-primary-600 text-white hover:bg-primary-700 shadow-sm hover:shadow',
    secondary:
      'border-2 border-primary-600 text-primary-600 hover:bg-primary-50',
    ghost: 'text-primary-600 hover:bg-primary-50 hover:text-primary-700',
  };

  return (
    <Link
      to={route}
      className={clsx(baseStyles, styleVariants[style], className)}
    >
      {IconComponent && <IconComponent className="w-4 h-4" />}
      {label}
      {!IconComponent && style === 'ghost' && (
        <ArrowRight className="w-4 h-4" />
      )}
    </Link>
  );
}

